import i18n from "i18next";

import { initReactI18next } from "react-i18next";

import deCommon from "./locales/de/common.json";
import deGame from "./locales/de/game.json";
import deAuth from "./locales/de/auth.json";
import deTutorial from "./locales/de/tutorial.json";
import deLanding from "./locales/de/landing.json";
import deLegal from "./locales/de/legal.json";
import enCommon from "./locales/en/common.json";
import enGame from "./locales/en/game.json";
import enAuth from "./locales/en/auth.json";
import enTutorial from "./locales/en/tutorial.json";
import enLanding from "./locales/en/landing.json";
import enLegal from "./locales/en/legal.json";

export const SUPPORTED_LOCALES = [
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "en", label: "English", flag: "🇬🇧" },
] as const;

export type LocaleCode = (typeof SUPPORTED_LOCALES)[number]["code"];

export const DEFAULT_LOCALE: LocaleCode = "de";

const resources = {
  de: { common: deCommon, game: deGame, auth: deAuth, tutorial: deTutorial, landing: deLanding, legal: deLegal },
  en: { common: enCommon, game: enGame, auth: enAuth, tutorial: enTutorial, landing: enLanding, legal: enLegal },
} as const;

export const LOCALE_STORAGE_KEY = "xenomons.locale";

if (!i18n.isInitialized) {
  // Always init with DEFAULT_LOCALE so SSR and the very first client render match.
  // Die bevorzugte Sprache kommt erst dazu, wenn auch der Seiteninhalt hydriert ist
  // (applyDetectedLanguage über afterFirstPageRender in __root.tsx).
  i18n.use(initReactI18next).init({
    resources,
    fallbackLng: DEFAULT_LOCALE,
    lng: DEFAULT_LOCALE,
    supportedLngs: SUPPORTED_LOCALES.map((l) => l.code),
    defaultNS: "common",
    ns: ["common", "game", "auth", "tutorial", "landing", "legal"],
    interpolation: { escapeValue: false },
    returnNull: false,
  });
}

/**
 * Schaltet auf die bevorzugte Sprache um. Nur über `afterFirstPageRender`
 * aufrufen, nicht direkt aus einem Effekt der Root-Route: Der läuft, bevor der
 * Seiteninhalt hydriert ist, und dann passt dessen Text nicht mehr zum Server.
 */
export function applyDetectedLanguage(): void {
  if (typeof window === "undefined") return;
  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    let target: string | null = stored;
    if (!target) {
      const nav = window.navigator.language?.slice(0, 2).toLowerCase();
      if (nav && SUPPORTED_LOCALES.some((l) => l.code === nav)) target = nav;
    }
    if (target && target !== i18n.language && SUPPORTED_LOCALES.some((l) => l.code === target)) {
      void i18n.changeLanguage(target);
    }
  } catch {
    /* ignore */
  }
}

export function persistLocale(code: LocaleCode): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, code);
  } catch {
    /* ignore */
  }
}

export default i18n;
